import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  X,
  Eye,
  AlertCircle,
  CheckCircle,
  Target,
  Loader2,
  AlertTriangle,
  Edit3,
  Save,
  RefreshCw,
  FileText,
  Shield,
  Bus,
  Car,
  Bike,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import policyService from "../../api/PolicyService";
import Loader from "../../components/Loader";
import ConfirmationModal from "../../components/ConfirmationModal";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";
import { StepperNav } from "../../components/policy/StepperNav";

import {
  StepBasicInfo,
  StepCoverage,
  StepBenefitsExclusions,
  StepTermsAndConditions,
  StepReview,
  isFixedDurationPolicy,
  validateFixedCoveragePrices,
  buildFixedCoverageDurations,
  parseFixedCoveragePrices,
} from "../../components/PolicyFormComponents";

export default function EditPolicy() {
  const navigate = useNavigate();
  const { policy_slug } = useParams();
  const user = useSelector((state) => state.auth.trp_usr);
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck();

  const getPolicyName = useCallback(
    (insuranceType, subcategory = "") => {
      let companyName = user?.company_name || "Company";
      const insuranceRegex = /\s+insurance\s*$/i;
      companyName = companyName.replace(insuranceRegex, "").trim();

      if (insuranceType === "tripsecure") {
        return "TripSecure Road Trip Insurance";
      } else if (insuranceType === "travel") {
        return `${companyName} Travel Insurance`;
      } else if (insuranceType === "life") {
        return `${companyName} Life Insurance`;
      } else if (insuranceType === "general") {
        if (subcategory) {
          const subcategoryNames = {
            health_insurance: "Health Insurance",
            home_insurance: "Home Insurance",
            accident_insurance: "Accident Insurance",
            fire_insurance: "Fire Insurance",
            marine_insurance: "Marine Insurance",
            liability_insurance: "Liability Insurance",
          };
          const subcategoryName =
            subcategoryNames[subcategory] || subcategory.replace(/_/g, " ");
          return `${companyName} ${subcategoryName}`;
        }
        return `${companyName} General Insurance`;
      }
      return "TripSecure Road Trip Insurance";
    },
    [user]
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    insuranceType: "travel",
    policyName: "TripSecure Road Trip Insurance",
    policyType: "",
    description: "",
    shortDescription: "",

    geography: "",
    regionalRoutes: {},
    internationalRoutes: {},
    sevenDayPrice: "",
    oneMonthPrice: "",

    subcategory: "",
    packages: [],

    insuranceCoverageList: [],
    exclusionsList: [],

    // Provider-managed fields for Travel, Life, and General Insurance
    claimProcessSteps: [],
    termsAndConditions: "",
      termsInputType: "text", // "text" or "url"
    eligibilityCriteria: [],

    max_claims: 3,
  });

  const [originalPolicy, setOriginalPolicy] = useState(null);
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const steps = [
    {
      id: 1,
      title: "Basic Info",
      subtitle: "Policy fundamentals",
      icon: FileText,
      color: "blue",
    },
    {
      id: 2,
      title: "Coverage",
      subtitle: "Routes & pricing",
      icon: Shield,
      color: "green",
    },
    {
      id: 3,
      title: "Benefits & Exclusions",
      subtitle: "What's covered",
      icon: Target,
      color: "purple",
    },
    {
      id: 4,
      title: "Terms & Conditions",
      subtitle: "Policy terms",
      icon: FileText,
      color: "teal",
    },
    {
      id: 5,
      title: "Review",
      subtitle: "Final changes",
      icon: Eye,
      color: "indigo",
    },
  ];

  const policyTypes = [
    {
      id: "daily_commuter",
      name: "Daily Commuter",
      description:
        "Perfect for everyday urban transportation and short-distance travel",
      icon: Bus,
      color: "blue",
      disabled: false,
      features: [
        "7-day and 1-month coverage",
        "Everyday urban travel",
        "Multi-modal transport",
        "City-wide protection",
      ],
    },
    {
      id: "intercity_travel",
      name: "Intercity Travel",
      description:
        "Comprehensive coverage for long-distance journeys and highway travel",
      icon: Car,
      color: "green",
      disabled: false,
      features: [
        "Regional & International routes",
        "Flexible route selection",
        "Highway incidents coverage",
        "Extended journey protection",
      ],
    },
    {
      id: "okada_rider",
      name: "Motor Rider",
      description: "Specialized protection for motorcycle transport and riders",
      icon: Bike,
      color: "orange",
      disabled: false,
      features: [
        "7-day and 1-month coverage",
        "Motorcycle-specific protection",
        "High-risk areas covered",
        "Competitive pricing",
      ],
    },
  ];
  const transformPolicyToFormData = useCallback(
    (policyData) => {
      const policy = policyData?.data?.data || policyData;

      // Determine insurance type from policy
      let insuranceType = "travel"; // default

      // Check insurance_type first
      if (policy.insurance_type) {
        if (policy.insurance_type === "life_insurance") {
          insuranceType = "life";
        } else if (policy.insurance_type === "general_insurance") {
          insuranceType = "general";
        } else if (policy.insurance_type === "travel_insurance") {
          insuranceType = "travel";
        } else if (policy.insurance_type === "tripsecure_insurance") {
          insuranceType = "tripsecure";
        }
      }
      // Check short_description if insurance_type is not available
      else if (policy.short_description) {
        if (policy.short_description === "tripsecure_insurance") {
          insuranceType = "tripsecure";
        } else if (policy.short_description === "life_insurance") {
          insuranceType = "life";
        } else if (policy.short_description === "general_insurance") {
          insuranceType = "general";
        } else if (policy.short_description === "travel_insurance") {
          insuranceType = "travel";
        }
      }
      // Fallback to policy_type
      else if (policy.policy_type === "life_insurance") {
        insuranceType = "life";
      } else if (
        policy.policy_type &&
        [
          "health_insurance",
          "home_insurance",
          "accident_insurance",
          "fire_insurance",
          "marine_insurance",
          "liability_insurance",
        ].includes(policy.policy_type)
      ) {
        insuranceType = "general";
      }

      // Determine subcategory for general insurance
      let subcategory = "";
      if (insuranceType === "general" && policy.policy_type) {
        subcategory = policy.policy_type;
      }

      // Determine policy type for travel insurance or tripsecure insurance
      let policyType = "";
      if (insuranceType === "travel" || insuranceType === "tripsecure") {
        policyType = policy.policy_type || "";
      }

      const REGIONAL_ROUTE_NAMES = [
        "Volta Region",
        "Eastern Region",
        "Ashanti Region",
        "Central Region",
        "Western Region",
        "Upper West Region",
      ];
      const INTERNATIONAL_ROUTE_NAMES = [
        "Togo",
        "Benin",
        "Nigeria",
        "Ivory Coast",
        "Burkina Faso",
      ];

      let regionalRoutes = {};
      let internationalRoutes = {};
      let sevenDayPrice = "";
      let oneMonthPrice = "";
      let geography = policy.geography || "";
      let packages = [];

      if (policy.coverage_durations) {
        try {
          const apiDurations = Array.isArray(policy.coverage_durations)
            ? policy.coverage_durations
            : JSON.parse(policy.coverage_durations);

          if (insuranceType === "tripsecure") {
            // TripSecure Insurance - intercity_travel or okada_rider
            if (policyType === "intercity_travel") {
              apiDurations.forEach((duration) => {
                const destination = duration.route_destination;
                const price = duration.price?.toString() || "";
                const routeType = duration.route_type;

                if (
                  destination &&
                  routeType === "regional" &&
                  REGIONAL_ROUTE_NAMES.includes(destination)
                ) {
                  regionalRoutes[destination] = { selected: true, price };
                } else if (
                  destination &&
                  routeType === "international" &&
                  INTERNATIONAL_ROUTE_NAMES.includes(destination)
                ) {
                  internationalRoutes[destination] = { selected: true, price };
                }
              });
            } else if (isFixedDurationPolicy(policyType)) {
              const parsed = parseFixedCoveragePrices(apiDurations);
              sevenDayPrice = parsed.sevenDayPrice;
              oneMonthPrice = parsed.oneMonthPrice;
            }
          } else if (insuranceType === "travel") {
            // Travel Insurance - check if it's intercity_travel or travel_insurance with packages
            if (policyType === "intercity_travel") {
              // Handle intercity_travel with routes
              apiDurations.forEach((duration) => {
                const destination = duration.route_destination;
                const price = duration.price?.toString() || "";
                const routeType = duration.route_type;

                if (
                  destination &&
                  routeType === "regional" &&
                  REGIONAL_ROUTE_NAMES.includes(destination)
                ) {
                  regionalRoutes[destination] = { selected: true, price };
                } else if (
                  destination &&
                  routeType === "international" &&
                  INTERNATIONAL_ROUTE_NAMES.includes(destination)
                ) {
                  internationalRoutes[destination] = { selected: true, price };
                }
              });
            } else if (policyType === "travel_insurance" || !policyType) {
              // Travel Insurance with packages (product_name, premium, duration string, description)
              packages = apiDurations.map((pkg, index) => {
                // Parse duration string (e.g., "3 hours", "2 weeks", "1 month", "1 year")
                let durationValue = "";
                let durationUnit = "hours";
                if (pkg.duration) {
                  const durationStr = pkg.duration.toString().trim();
                  // Match patterns like "3 hours", "2 weeks", "1 month", "1 year", "1 day", etc.
                  const durationMatch = durationStr.match(
                    /^(\d+)\s*(hours?|days?|weeks?|months?|years?|weekly|monthly|yearly)$/i
                  );
                  if (durationMatch) {
                    durationValue = durationMatch[1];
                    const unit = durationMatch[2].toLowerCase();
                    // Normalize units to new standardized format
                    if (unit.startsWith("hour")) {
                      durationUnit = unit === "hour" ? "hour" : "hours";
                    } else if (unit.startsWith("day")) {
                      durationUnit = unit === "day" ? "day" : "days";
                    } else if (unit.startsWith("week")) {
                      // Handle legacy "weekly" format
                      if (unit === "weekly") {
                        durationUnit = "weeks";
                      } else {
                        durationUnit = unit === "week" ? "week" : "weeks";
                      }
                    } else if (unit.startsWith("month")) {
                      // Handle legacy "monthly" format
                      if (unit === "monthly") {
                        durationUnit = "months";
                      } else {
                        durationUnit = unit === "month" ? "month" : "months";
                      }
                    } else if (unit.startsWith("year")) {
                      // Handle legacy "yearly" format
                      if (unit === "yearly") {
                        durationUnit = "years";
                      } else {
                        durationUnit = "years";
                      }
                    }
                  }
                }

                // Parse package-level policy fields if present
                // insurance_coverage_list -> insuranceCoverageList
                let pkgInsuranceCoverageList = [];
                if (pkg.insurance_coverage_list) {
                  try {
                    const apiCoverage = Array.isArray(pkg.insurance_coverage_list)
                      ? pkg.insurance_coverage_list
                      : JSON.parse(pkg.insurance_coverage_list);
                    pkgInsuranceCoverageList = apiCoverage.map((coverage, idx) => ({
                      id: Date.now() + index + idx + Math.random(),
                      title: coverage.title || "",
                      description: coverage.description || "",
                      limit: coverage.limit || "",
                      type: coverage.coverage_type || "general",
                    }));
                  } catch {}
                }

                // exclusions -> exclusionsList
                let pkgExclusionsList = [];
                if (pkg.exclusions) {
                  try {
                    const apiExclusions = Array.isArray(pkg.exclusions)
                      ? pkg.exclusions
                      : JSON.parse(pkg.exclusions);
                    pkgExclusionsList = apiExclusions.map((exclusion, idx) => {
                      const text =
                        typeof exclusion === "string" ? exclusion : String(exclusion || "");
                      return {
                        id: Date.now() + index + idx + Math.random(),
                        text: text,
                        type: "general",
                      };
                    });
                  } catch {}
                }

                // eligibility_criteria
                let pkgEligibilityCriteria = [];
                if (pkg.eligibility_criteria) {
                  try {
                    const apiCriteria = Array.isArray(pkg.eligibility_criteria)
                      ? pkg.eligibility_criteria
                      : JSON.parse(pkg.eligibility_criteria);
                    pkgEligibilityCriteria = apiCriteria.map((c) => String(c || ""));
                  } catch {}
                }

                // claim_process_steps
                let pkgClaimProcessSteps = [];
                if (pkg.claim_process_steps) {
                  try {
                    const apiSteps = Array.isArray(pkg.claim_process_steps)
                      ? pkg.claim_process_steps
                      : JSON.parse(pkg.claim_process_steps);
                    pkgClaimProcessSteps = apiSteps.map((step) => ({
                      step_number: step.step_number || 0,
                      title: step.title || "",
                      description: step.description || "",
                      timeframe: step.timeframe || "",
                      requirements: step.requirements || "",
                    }));
                  } catch {}
                }

                // terms
                let pkgTermsAndConditions = "";
                let pkgTermsInputType = "text";
                if (pkg.terms) {
                  const t = String(pkg.terms);
                  if (t.startsWith("http://") || t.startsWith("https://")) {
                    pkgTermsInputType = "url";
                    pkgTermsAndConditions = t;
                  } else {
                    pkgTermsInputType = "text";
                    pkgTermsAndConditions = t;
                  }
                }

                const quoteRequestFields = (pkg.quote_request_fields || []).map((qf, qidx) => ({
                  id: Date.now() + index + qidx + Math.random(),
                  key: qf.key || "",
                  label: qf.label || "",
                  type: qf.type || "text",
                  required: !!qf.required,
                  options: Array.isArray(qf.options) ? qf.options : [],
                }));

                return {
                  id: Date.now() + index + Math.random(),
                  productName: pkg.product_name || "",
                  premium: pkg.premium?.toString() || "",
                  premiumDuration: pkg.premium_duration || "monthly",
                  durationValue: durationValue,
                  durationUnit: durationUnit,
                  description: pkg.description || "",
                  max_claims: pkg.max_claims ?? policy.max_claims ?? 3,
                  is_quote_only: !!pkg.is_quote_only,
                  quote_request_fields: quoteRequestFields,
                  // Store with both field name formats for compatibility
                  insurance_coverage_list: pkgInsuranceCoverageList,
                  insuranceCoverageList: pkgInsuranceCoverageList, // Keep for backward compatibility
                  exclusions: pkgExclusionsList,
                  exclusionsList: pkgExclusionsList, // Keep for backward compatibility
                  eligibility_criteria: pkgEligibilityCriteria,
                  eligibilityCriteria: pkgEligibilityCriteria, // Keep for backward compatibility
                  claim_process_steps: pkgClaimProcessSteps,
                  claimProcessSteps: pkgClaimProcessSteps, // Keep for backward compatibility
                  terms: pkgTermsAndConditions,
                  termsAndConditions: pkgTermsAndConditions, // Keep for backward compatibility
                  termsInputType: pkgTermsInputType,
                };
              });
            }
          } else if (insuranceType === "life" || insuranceType === "general") {
            // Extract packages from coverage_durations
            packages = apiDurations.map((pkg, index) => {
              // Parse duration string (e.g., "5 years" or "12 months" or "1 day", etc.)
              let durationValue = "";
              let durationUnit = "years";
              if (pkg.duration) {
                const durationMatch = pkg.duration
                  .toString()
                  .match(/^(\d+)\s*(hours?|days?|weeks?|months?|years?|weekly|monthly|yearly)$/i);
                if (durationMatch) {
                  durationValue = durationMatch[1];
                  const unit = durationMatch[2].toLowerCase();
                  // Normalize units to new standardized format
                  if (unit.startsWith("hour")) {
                    durationUnit = unit === "hour" ? "hour" : "hours";
                  } else if (unit.startsWith("day")) {
                    durationUnit = unit === "day" ? "day" : "days";
                  } else if (unit.startsWith("week")) {
                    if (unit === "weekly") {
                      durationUnit = "weeks";
                    } else {
                      durationUnit = unit === "week" ? "week" : "weeks";
                    }
                  } else if (unit.startsWith("month")) {
                    if (unit === "monthly") {
                      durationUnit = "months";
                    } else {
                      durationUnit = unit === "month" ? "month" : "months";
                    }
                  } else if (unit.startsWith("year")) {
                    if (unit === "yearly") {
                      durationUnit = "years";
                    } else {
                      durationUnit = "years";
                    }
                  }
                }
              }

              // Parse package-level policy fields if present
              let pkgInsuranceCoverageList = [];
              if (pkg.insurance_coverage_list) {
                try {
                  const apiCoverage = Array.isArray(pkg.insurance_coverage_list)
                    ? pkg.insurance_coverage_list
                    : JSON.parse(pkg.insurance_coverage_list);
                  pkgInsuranceCoverageList = apiCoverage.map((coverage, idx) => ({
                    id: Date.now() + index + idx + Math.random(),
                    title: coverage.title || "",
                    description: coverage.description || "",
                    limit: coverage.limit || "",
                    type: coverage.coverage_type || "general",
                  }));
                } catch {}
              }

              let pkgExclusionsList = [];
              if (pkg.exclusions) {
                try {
                  const apiExclusions = Array.isArray(pkg.exclusions)
                    ? pkg.exclusions
                    : JSON.parse(pkg.exclusions);
                  pkgExclusionsList = apiExclusions.map((exclusion, idx) => {
                    const text =
                      typeof exclusion === "string" ? exclusion : String(exclusion || "");
                    return {
                      id: Date.now() + index + idx + Math.random(),
                      text: text,
                      type: "general",
                    };
                  });
                } catch {}
              }

              let pkgEligibilityCriteria = [];
              if (pkg.eligibility_criteria) {
                try {
                  const apiCriteria = Array.isArray(pkg.eligibility_criteria)
                    ? pkg.eligibility_criteria
                    : JSON.parse(pkg.eligibility_criteria);
                  pkgEligibilityCriteria = apiCriteria.map((c) => String(c || ""));
                } catch {}
              }

              let pkgClaimProcessSteps = [];
              if (pkg.claim_process_steps) {
                try {
                  const apiSteps = Array.isArray(pkg.claim_process_steps)
                    ? pkg.claim_process_steps
                    : JSON.parse(pkg.claim_process_steps);
                  pkgClaimProcessSteps = apiSteps.map((step) => ({
                    step_number: step.step_number || 0,
                    title: step.title || "",
                    description: step.description || "",
                    timeframe: step.timeframe || "",
                    requirements: step.requirements || "",
                  }));
                } catch {}
              }

              let pkgTermsAndConditions = "";
              let pkgTermsInputType = "text";
              if (pkg.terms) {
                const t = String(pkg.terms);
                if (t.startsWith("http://") || t.startsWith("https://")) {
                  pkgTermsInputType = "url";
                  pkgTermsAndConditions = t;
                } else {
                  pkgTermsInputType = "text";
                  pkgTermsAndConditions = t;
                }
              }

              const quoteRequestFields = (pkg.quote_request_fields || []).map((qf, qidx) => ({
                id: Date.now() + index + qidx + Math.random(),
                key: qf.key || "",
                label: qf.label || "",
                type: qf.type || "text",
                required: !!qf.required,
                options: Array.isArray(qf.options) ? qf.options : [],
              }));

              return {
                id: Date.now() + index + Math.random(),
                productName: pkg.product_name || "",
                premium: pkg.premium?.toString() || "",
                premiumDuration: pkg.premium_duration || "monthly",
                durationValue: durationValue,
                durationUnit: durationUnit,
                description: pkg.description || "",
                max_claims: pkg.max_claims ?? policy.max_claims ?? 3,
                is_quote_only: !!pkg.is_quote_only,
                quote_request_fields: quoteRequestFields,
                // Store with both field name formats for compatibility
                insurance_coverage_list: pkgInsuranceCoverageList,
                insuranceCoverageList: pkgInsuranceCoverageList, // Keep for backward compatibility
                exclusions: pkgExclusionsList,
                exclusionsList: pkgExclusionsList, // Keep for backward compatibility
                eligibility_criteria: pkgEligibilityCriteria,
                eligibilityCriteria: pkgEligibilityCriteria, // Keep for backward compatibility
                claim_process_steps: pkgClaimProcessSteps,
                claimProcessSteps: pkgClaimProcessSteps, // Keep for backward compatibility
                terms: pkgTermsAndConditions,
                termsAndConditions: pkgTermsAndConditions, // Keep for backward compatibility
                termsInputType: pkgTermsInputType,
              };
            });
          }
        } catch (error) {
          console.warn("Failed to parse coverage_durations:", error);
        }
      }

      // Parse insurance coverage list (benefits)
      let insuranceCoverageList = [];
      if (policy.insurance_coverage_list) {
        try {
          const apiCoverage = Array.isArray(policy.insurance_coverage_list)
            ? policy.insurance_coverage_list
            : JSON.parse(policy.insurance_coverage_list);

          insuranceCoverageList = apiCoverage.map((coverage, index) => ({
            id: Date.now() + index + Math.random(),
            title: coverage.title || "",
            description: coverage.description || "",
            limit: coverage.limit || "",
            type: coverage.coverage_type || "general",
          }));
        } catch (error) {
          console.warn("Failed to parse insurance_coverage_list:", error);
        }
      }

      // Parse exclusions back to exclusionsList format
      let exclusionsList = [];
      if (policy.exclusions) {
        try {
          const apiExclusions = Array.isArray(policy.exclusions)
            ? policy.exclusions
            : JSON.parse(policy.exclusions);

          exclusionsList = apiExclusions.map((exclusion, index) => {
            let text = exclusion;
            let type = "general";

            if (typeof exclusion === "string") {
              const regionalMatch = exclusion.match(/^\[REGIONAL\]\s*(.+)/i);
              const internationalMatch = exclusion.match(
                /^\[INTERNATIONAL\]\s*(.+)/i
              );

              if (regionalMatch) {
                type = "regional";
                text = regionalMatch[1];
              } else if (internationalMatch) {
                type = "international";
                text = internationalMatch[1];
              }
            }

            return {
              id: Date.now() + index + Math.random(),
              text: text,
              type: type,
            };
          });
        } catch (error) {
          console.warn("Failed to parse exclusions:", error);
        }
      }

      // Get policy name based on insurance type
      const policyName = getPolicyName(insuranceType, subcategory);

      // Parse eligibility criteria
      let eligibilityCriteria = [];
      if (policy.eligibility_criteria) {
        try {
          const apiCriteria = Array.isArray(policy.eligibility_criteria)
            ? policy.eligibility_criteria
            : JSON.parse(policy.eligibility_criteria);
          eligibilityCriteria = apiCriteria.map((c) => String(c || ""));
        } catch (error) {
          console.warn("Failed to parse eligibility_criteria:", error);
          // Fallback to empty array
          eligibilityCriteria = [];
        }
      }

      // Get terms and conditions and detect input type
      const termsText = policy.terms || "";
      let termsAndConditions = "";
      let termsInputType = "text";

      // Detect if terms is a URL
      if (termsText.startsWith("http://") || termsText.startsWith("https://")) {
        termsInputType = "url";
        termsAndConditions = termsText;
      } else {
        termsInputType = "text";
        termsAndConditions = termsText;
      }

      // Get claim process steps from claim_process_steps
      let claimProcessSteps = [];
      if (policy.claim_process_steps) {
        try {
          const apiSteps = Array.isArray(policy.claim_process_steps)
            ? policy.claim_process_steps
            : JSON.parse(policy.claim_process_steps);
          // Load structured steps
          claimProcessSteps = apiSteps.map((step) => ({
            step_number: step.step_number || 0,
            title: step.title || "",
            description: step.description || "",
            timeframe: step.timeframe || "",
            requirements: step.requirements || "",
          }));
        } catch (error) {
          console.warn("Failed to parse claim_process_steps:", error);
        }
      }

      return {
        // Insurance Type
        insuranceType: insuranceType,
        policyName: policyName,
        policyType: policyType,
        description: policy.description || "",
        shortDescription: policy.short_description || "",

        // Coverage Details (Dynamic)
        geography: geography,
        regionalRoutes,
        internationalRoutes,
        sevenDayPrice,
        oneMonthPrice,

        // Life/General Insurance
        subcategory: subcategory,
        packages: packages,

        // Benefits & Exclusions
        insuranceCoverageList,
        exclusionsList,

        // Provider-managed fields for Travel, Life, and General Insurance
        eligibilityCriteria: eligibilityCriteria,
        termsAndConditions: termsAndConditions,
        termsInputType: termsInputType,
        claimProcessSteps: claimProcessSteps,

        max_claims: policy.max_claims ?? 3,
      };
    },
    [getPolicyName]
  );

  useEffect(() => {
    const fetchPolicy = async () => {
      if (!policy_slug) {
        setLoadError("No policy identifier provided");
        setIsLoadingPolicy(false);
        return;
      }

      setIsLoadingPolicy(true);
      setLoadError(null);

      try {
        const response = await policyService.getPolicy(policy_slug);
        const data = response?.data?.data?.data;

        if (data.status_code === 200 && data.data) {
          const policyData = data.data;
          setOriginalPolicy(policyData);

          const transformedData = transformPolicyToFormData(policyData);
          setFormData(transformedData);
        } else {
          setLoadError(data.reason || "Failed to fetch policy details");
        }
      } catch (error) {
        console.error("Error fetching policy:", error);
        setLoadError(
          "An unexpected error occurred while fetching policy details"
        );
      } finally {
        setIsLoadingPolicy(false);
      }
    };

    fetchPolicy();
  }, [policy_slug, transformPolicyToFormData]);

  useEffect(() => {
    if (originalPolicy && !isLoadingPolicy) {
      const currentFormString = JSON.stringify(formData);
      const originalFormString = JSON.stringify(
        transformPolicyToFormData(originalPolicy)
      );
      setHasUnsavedChanges(currentFormString !== originalFormString);
    }
  }, [formData, originalPolicy, isLoadingPolicy, transformPolicyToFormData]);

  const updateFormData = useCallback(
    (field, value) => {
      setFormData((prev) => {
        const newData = { ...prev, [field]: value };

        if (field === "insuranceType") {
          const previousInsuranceType = prev.insuranceType;
          const newInsuranceType = value;

          // If switching to Life or General Insurance, clear travel/tripsecure-specific fields
          if (newInsuranceType === "life" || newInsuranceType === "general") {
            newData.policyType = "";
            newData.geography = "";
            newData.regionalRoutes = {};
            newData.internationalRoutes = {};
            newData.sevenDayPrice = "";
            newData.oneMonthPrice = "";
          }

          // If switching to Travel Insurance, clear Life/General-specific fields
          if (newInsuranceType === "travel") {
            newData.packages = [];
            newData.subcategory = "";
          }

          // If switching to TripSecure Insurance, clear package fields
          if (newInsuranceType === "tripsecure") {
            newData.packages = [];
            newData.subcategory = "";
          }

          // Clear benefits and exclusions when switching insurance types
          if (
            previousInsuranceType &&
            previousInsuranceType !== newInsuranceType
          ) {
            newData.insuranceCoverageList = [];
            newData.exclusionsList = [];
          }

          // Update policy name based on insurance type
          newData.policyName = getPolicyName(
            newInsuranceType,
            newData.subcategory
          );
        }

        // Handle subcategory changes for General Insurance
        if (field === "subcategory" && prev.insuranceType === "general") {
          newData.policyName = getPolicyName(prev.insuranceType, value);
        }

        // Handle terms input type changes - clear data when switching types
        if (field === "termsInputType") {
          const previousType = prev.termsInputType;
          const newType = value;
          if (previousType !== newType) {
            // Clear the terms data when switching input types
            if (newType === "url") {
              // Clear text if switching to URL
              newData.termsAndConditions = "";
            }
          }
        }

        if (field === "geography") {
          const previousGeography = prev.geography;
          const newGeography = value;

          if (
            previousGeography === "both" &&
            (newGeography === "regional" || newGeography === "international")
          ) {
            newData.insuranceCoverageList =
              prev.insuranceCoverageList?.filter(
                (item) => item.type === newGeography || item.type === "general"
              ) || [];
            newData.exclusionsList =
              prev.exclusionsList?.filter(
                (item) => item.type === newGeography || item.type === "general"
              ) || [];
          } else if (
            (previousGeography === "regional" ||
              previousGeography === "international") &&
            newGeography === "both"
          ) {
            newData.insuranceCoverageList = (
              prev.insuranceCoverageList || []
            ).map((item) => ({
              ...item,
              type: item.type || previousGeography,
            }));
            newData.exclusionsList = (prev.exclusionsList || []).map(
              (item) => ({
                ...item,
                type: item.type || previousGeography,
              })
            );
          } else if (
            (previousGeography === "regional" &&
              newGeography === "international") ||
            (previousGeography === "international" &&
              newGeography === "regional")
          ) {
            newData.insuranceCoverageList = [];
            newData.exclusionsList = [];
          } else {
            newData.insuranceCoverageList = [];
            newData.exclusionsList = [];
          }
        }

        return newData;
      });

      // Clear validation error for this field
      setValidationErrors((prev) => {
        if (prev[field]) {
          const { [field]: removed, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [getPolicyName]
  );

  const handleNavigation = useCallback(
    (path) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(path);
        setShowUnsavedModal(true);
        return;
      }
      navigate(path);
    },
    [hasUnsavedChanges, navigate]
  );

  const handleUnsavedConfirm = () => {
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  // Validation
  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 1: // Basic Info
        if (
          formData.insuranceType === "travel" ||
          formData.insuranceType === "tripsecure"
        ) {
          if (!formData.policyType)
            errors.policyType = "Please select a policy type";
        }
        if (formData.insuranceType === "general" && !formData.subcategory) {
          errors.subcategory = "Please select a subcategory";
        }
        break;

      case 2: // Coverage
        if (
          formData.insuranceType === "travel" ||
          formData.insuranceType === "tripsecure"
        ) {
          if (formData.policyType === "intercity_travel") {
            if (!formData.geography) {
              errors.geography = "Please select a geographic coverage type";
            }

            // Check if at least one route is selected
            const hasRegionalRoute =
              formData.geography === "regional" ||
              formData.geography === "both";
            const hasInternationalRoute =
              formData.geography === "international" ||
              formData.geography === "both";

            const selectedRegionalRoutes = hasRegionalRoute
              ? Object.values(formData.regionalRoutes || {}).filter(
                  (r) => r.selected && r.price && parseFloat(r.price) >= 0.01
                )
              : [];

            const selectedInternationalRoutes = hasInternationalRoute
              ? Object.values(formData.internationalRoutes || {}).filter(
                  (r) => r.selected && r.price && parseFloat(r.price) >= 0.01
                )
              : [];

            if (
              selectedRegionalRoutes.length === 0 &&
              selectedInternationalRoutes.length === 0
            ) {
              errors.routes = "Please select and price at least one route";
            }

            // Validate regional route prices don't exceed max
            if (hasRegionalRoute) {
              const invalidRegionalPrices = Object.values(
                formData.regionalRoutes || {}
              ).filter((r) => r.selected && parseFloat(r.price) > 10);
              if (invalidRegionalPrices.length > 0) {
                errors.routes = "Regional route prices cannot exceed ₵10";
              }
            }

            // Validate international route prices don't exceed max
            if (hasInternationalRoute) {
              const invalidInternationalPrices = Object.values(
                formData.internationalRoutes || {}
              ).filter((r) => r.selected && parseFloat(r.price) > 20);
              if (invalidInternationalPrices.length > 0) {
                errors.routes = "International route prices cannot exceed ₵20";
              }
            }
          } else if (isFixedDurationPolicy(formData.policyType)) {
            validateFixedCoveragePrices(formData, errors);
          }
        } else if (
          formData.insuranceType === "life" ||
          formData.insuranceType === "general" ||
          (formData.insuranceType === "travel" &&
            (formData.policyType === "travel_insurance" ||
              !formData.policyType))
        ) {
          // Validate packages for Life, General, or Travel Insurance with packages
          if (!formData.packages || formData.packages.length === 0) {
            errors.packages = "Please add at least one package";
          } else {
            // Normalize packages to ensure durationUnit is set
            const normalizedPackages = formData.packages.map((pkg) => ({
              ...pkg,
              durationUnit:
                pkg.durationUnit ||
                (formData.insuranceType === "travel" ? "hours" : "years"),
            }));

            // Validate each package has required fields
            const invalidPackages = normalizedPackages.filter((pkg) => {
              const productName = pkg.productName?.trim() || "";
              const hasProductName = productName.length > 0;

              const isQuoteOnly = !!pkg.is_quote_only;
              const premiumStr = String(pkg.premium || "").trim();
              const premiumValue = premiumStr ? parseFloat(premiumStr) : NaN;
              const hasPremium = isQuoteOnly || (!isNaN(premiumValue) && premiumValue > 0);

              const premiumDuration = pkg.premiumDuration || "monthly";
              const hasPremiumDuration = isQuoteOnly || ["daily", "weekly", "monthly", "yearly"].includes(premiumDuration);

              // Check duration value - handle string and number inputs
              const durationValueStr = String(pkg.durationValue || "").trim();
              const durationValue = durationValueStr
                ? parseFloat(durationValueStr)
                : NaN;
              const hasDurationValue =
                !isNaN(durationValue) && durationValue > 0;

              const durationUnit =
                pkg.durationUnit ||
                (formData.insuranceType === "travel" ? "hours" : "years");
              let hasDurationUnit = false;
              if (formData.insuranceType === "travel" || formData.insuranceType === "life" || formData.insuranceType === "general") {
                hasDurationUnit = [
                  "hours",
                  "hour",
                  "day",
                  "days",
                  "week",
                  "weeks",
                  "month",
                  "months",
                  "years",
                ].includes(durationUnit);
              } else {
                hasDurationUnit =
                  durationUnit === "years" || durationUnit === "months";
              }

              // Check description
              const description = pkg.description?.trim() || "";
              const hasDescription = description.length > 0;

              return (
                !hasProductName ||
                !hasPremium ||
                !hasPremiumDuration ||
                !hasDurationValue ||
                !hasDurationUnit ||
                !hasDescription
              );
            });
            if (invalidPackages.length > 0) {
              const durationUnitsText =
                formData.insuranceType === "travel" || formData.insuranceType === "life" || formData.insuranceType === "general"
                  ? "hours, hour, day, days, week, weeks, month, months, or years"
                  : "years or months";
              errors.packages = `All packages must have Product name, Premium (greater than 0) or "Quote-Based Only", Payment Term (daily/weekly/monthly/yearly), Package Duration (number greater than 0 and unit: ${durationUnitsText}), and Description`;
            }
          }
        }
        break;

      case 3: // Benefits & Exclusions
        if (
          !formData.insuranceCoverageList ||
          formData.insuranceCoverageList.length === 0
        ) {
          errors.benefits = "Please add at least one coverage benefit";
        } else {
          // Check if benefits have required fields
          const invalidBenefits = formData.insuranceCoverageList.filter(
            (b) => !b.title?.trim() || !b.description?.trim()
          );
          if (invalidBenefits.length > 0) {
            errors.benefits = "All benefits must have a title and description";
          }
        }

        // Validate provider-managed fields for Travel, Life, and General Insurance
        if (
          formData.insuranceType === "travel" ||
          formData.insuranceType === "life" ||
          formData.insuranceType === "general"
        ) {
          // Validate eligibility criteria
          const validCriteria = (formData.eligibilityCriteria || []).filter(
            (c) => c && c.trim() !== ""
          );
          if (validCriteria.length === 0) {
            errors.eligibilityCriteria =
              "Please add at least one eligibility criterion";
          }

          // Validate claim process steps
          const validSteps = (formData.claimProcessSteps || []).filter(
            (step) =>
              step.title?.trim() &&
              step.description?.trim() &&
              step.timeframe?.trim()
          );
          if (validSteps.length === 0) {
            errors.claimProcessSteps =
              "Please add at least one claim process step with title, description, and timeframe";
          }
        }
        break;

      case 4: // Terms and Conditions
        // Validate terms and conditions for Travel, Life, and General Insurance
        if (
          formData.insuranceType === "travel" ||
          formData.insuranceType === "life" ||
          formData.insuranceType === "general"
        ) {
          const inputType = formData.termsInputType || "text";
          if (inputType === "url") {
            if (!formData.termsAndConditions?.trim()) {
              errors.termsAndConditions =
                "Please provide a URL link to your terms and conditions";
            } else {
              // Basic URL validation
              try {
                new URL(formData.termsAndConditions);
              } catch {
                errors.termsAndConditions =
                  "Please provide a valid URL (e.g., https://example.com/terms)";
              }
            }
          } else {
            // Text input type
            if (!formData.termsAndConditions?.trim()) {
              errors.termsAndConditions =
                "Please provide terms and conditions for this policy";
            }
          }
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Comprehensive validation for final submission
  const validateAllSteps = () => {
    const isTripSecure = formData.insuranceType === "tripsecure";
    // For TripSecure, validate steps 1-3 (skip step 4 - Terms)
    // For non-TripSecure, validate steps 1-2 (steps 3 & 4 are per-package now)
    const maxStep = isTripSecure ? 3 : 2;
    for (let i = 1; i <= maxStep; i++) {
      if (!validateStep(i)) {
        return false;
      }
    }
    return true;
  };

  // Navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      const isTripSecure = formData.insuranceType === "tripsecure";
      setCurrentStep((prev) => {
        if (isTripSecure) {
          // TripSecure: Skip step 4 (Terms)
          if (prev === 3) {
            return 5; // Go directly to Review
          }
        } else {
          // Non-TripSecure: Skip steps 3 & 4 (Benefits & Terms are per-package)
          if (prev === 2) {
            return 5; // Go directly to Review after Coverage
          }
        }
        return Math.min(prev + 1, steps.length);
      });
    }
  };

  const prevStep = () => {
    const isTripSecure = formData.insuranceType === "tripsecure";
    setCurrentStep((prev) => {
      if (isTripSecure) {
        // TripSecure: Skip step 4 (Terms) when going back
        if (prev === 5) {
          return 3; // Go back to Benefits & Exclusions
        }
      } else {
        // Non-TripSecure: Skip steps 3 & 4 when going back
        if (prev === 5) {
          return 2; // Go back to Coverage
        }
      }
      return Math.max(prev - 1, 1);
    });
  };

  const goToStep = (step) => {
    if (step <= currentStep || validateStep(currentStep)) {
      const isTripSecure = formData.insuranceType === "tripsecure";
      let actualStep = step;
      
      if (isTripSecure) {
        // Map display step to actual step for TripSecure (skip step 4)
        actualStep = step === 4 ? 5 : step;
      } else {
        // Map display step to actual step for non-TripSecure (skip steps 3 & 4)
        if (step === 3) {
          actualStep = 5; // Map to Review
        }
      }
      
      setCurrentStep(actualStep);
    }
  };

  const renderCurrentStep = () => {
    const isTripSecure = formData.insuranceType === "tripsecure";
    
    switch (currentStep) {
      case 1:
        return (
          <StepBasicInfo
            formData={formData}
            updateFormData={updateFormData}
            policyTypes={policyTypes}
            validationErrors={validationErrors}
          />
        );
      case 2:
        return (
          <StepCoverage
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 3:
        return (
          <StepBenefitsExclusions
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 4:
        // Only show Terms step for non-TripSecure insurance
        if (isTripSecure) {
          return <StepReview formData={formData} policyTypes={policyTypes} />;
        }
        return (
          <StepTermsAndConditions
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 5:
        return <StepReview formData={formData} policyTypes={policyTypes} />;
      default:
        return (
          <StepBasicInfo
            formData={formData}
            updateFormData={updateFormData}
            policyTypes={policyTypes}
            validationErrors={validationErrors}
          />
        );
    }
  };

  const transformFormDataToPayload = () => {
    let coverageDurationsInHours = [];
    let geography = formData.geography || "";

    // Handle TripSecure Insurance
    if (formData.insuranceType === "tripsecure") {
      if (formData.policyType === "intercity_travel") {
        const hasRegionalRoute =
          geography === "regional" || geography === "both";
        const hasInternationalRoute =
          geography === "international" || geography === "both";

        if (hasRegionalRoute) {
          Object.entries(formData.regionalRoutes || {}).forEach(
            ([destination, data]) => {
              if (data.selected && data.price) {
                // Find the route config to get hours
                const REGIONAL_ROUTES = [
                  { destination: "Volta Region", hours: 6 },
                  { destination: "Eastern Region", hours: 4 },
                  { destination: "Ashanti Region", hours: 8 },
                  { destination: "Central Region", hours: 6 },
                  { destination: "Western Region", hours: 8 },
                  { destination: "Upper West Region", hours: 20 },
                ];
                const routeConfig = REGIONAL_ROUTES.find(
                  (r) => r.destination === destination
                );
                if (routeConfig) {
                  coverageDurationsInHours.push({
                    hours: routeConfig.hours,
                    price: parseFloat(data.price),
                    display_unit: "hours",
                    display_value: routeConfig.hours,
                    route_destination: destination,
                    route_type: "regional",
                  });
                }
              }
            }
          );
        }

        if (hasInternationalRoute) {
          Object.entries(formData.internationalRoutes || {}).forEach(
            ([destination, data]) => {
              if (data.selected && data.price) {
                const INTERNATIONAL_ROUTES = [
                  { destination: "Togo", hours: 8 },
                  { destination: "Benin", hours: 12 },
                  { destination: "Nigeria", hours: 24 },
                  { destination: "Ivory Coast", hours: 15 },
                  { destination: "Burkina Faso", hours: 20 },
                ];
                const routeConfig = INTERNATIONAL_ROUTES.find(
                  (r) => r.destination === destination
                );
                if (routeConfig) {
                  coverageDurationsInHours.push({
                    hours: routeConfig.hours,
                    price: parseFloat(data.price),
                    display_unit: "hours",
                    display_value: routeConfig.hours,
                    route_destination: destination,
                    route_type: "international",
                  });
                }
              }
            }
          );
        }
      } else if (isFixedDurationPolicy(formData.policyType)) {
        coverageDurationsInHours = buildFixedCoverageDurations(formData);
        geography = "national";
      }
    } else if (formData.insuranceType === "travel") {
      // Handle Travel Insurance
      if (formData.policyType === "intercity_travel") {
        // Travel Insurance with intercity_travel (routes)
        const hasRegionalRoute =
          geography === "regional" || geography === "both";
        const hasInternationalRoute =
          geography === "international" || geography === "both";

        if (hasRegionalRoute) {
          Object.entries(formData.regionalRoutes || {}).forEach(
            ([destination, data]) => {
              if (data.selected && data.price) {
                const REGIONAL_ROUTES = [
                  { destination: "Volta Region", hours: 6 },
                  { destination: "Eastern Region", hours: 4 },
                  { destination: "Ashanti Region", hours: 8 },
                  { destination: "Central Region", hours: 6 },
                  { destination: "Western Region", hours: 8 },
                  { destination: "Upper West Region", hours: 20 },
                ];
                const routeConfig = REGIONAL_ROUTES.find(
                  (r) => r.destination === destination
                );
                if (routeConfig) {
                  coverageDurationsInHours.push({
                    hours: routeConfig.hours,
                    price: parseFloat(data.price),
                    display_unit: "hours",
                    display_value: routeConfig.hours,
                    route_destination: destination,
                    route_type: "regional",
                  });
                }
              }
            }
          );
        }

        if (hasInternationalRoute) {
          Object.entries(formData.internationalRoutes || {}).forEach(
            ([destination, data]) => {
              if (data.selected && data.price) {
                const INTERNATIONAL_ROUTES = [
                  { destination: "Togo", hours: 8 },
                  { destination: "Benin", hours: 12 },
                  { destination: "Nigeria", hours: 24 },
                  { destination: "Ivory Coast", hours: 15 },
                  { destination: "Burkina Faso", hours: 20 },
                ];
                const routeConfig = INTERNATIONAL_ROUTES.find(
                  (r) => r.destination === destination
                );
                if (routeConfig) {
                  coverageDurationsInHours.push({
                    hours: routeConfig.hours,
                    price: parseFloat(data.price),
                    display_unit: "hours",
                    display_value: routeConfig.hours,
                    route_destination: destination,
                    route_type: "international",
                  });
                }
              }
            }
          );
        }
      } else if (
        formData.policyType === "travel_insurance" ||
        !formData.policyType
      ) {
        // Travel Insurance with packages will be assigned later with per-package policy fields
      }
    }

    // NOTE: For Life/General or Travel with packages, coverageDurationsInHours will be assigned later

    const benefits = formData.insuranceCoverageList.map((benefit) => ({
      title: benefit.title?.trim() || "",
      description: benefit.description?.trim() || "",
      limit: benefit.limit?.trim() || "",
      ...(benefit.type && benefit.type !== "general"
        ? {
            coverage_type: benefit.type,
          }
        : {}),
    }));

    const exclusions = (formData.exclusionsList || [])
      .map((exclusion) => {
        const text = exclusion.text?.trim() || "";
        if (exclusion.type && exclusion.type !== "general") {
          return `[${exclusion.type.toUpperCase()}] ${text}`;
        }
        return text;
      })
      .filter((e) => e !== "");

    // Hardcoded defaults for TripSecure Insurance
    const defaultEligibilityCriteria = [
      "Must be 18 years or older",
      "Valid identification required",
      "Journey must originate from Greater Accra region",
    ];

    const defaultTerms =
      "This policy is governed by TripSecure's standard terms and conditions. Coverage is valid for the specified duration and route only.";

    const defaultClaimSteps = [
      {
        step_number: 1,
        title: "Report Incident",
        description:
          "Contact TripSecure support immediately after the incident",
        timeframe: "Within 24 hours",
        requirements: "Incident details and location",
      },
      {
        step_number: 2,
        title: "Submit Documentation",
        description: "Provide all required documents and evidence",
        timeframe: "Within 7 days",
        requirements:
          "Police report (if applicable), photos, witness statements",
      },
      {
        step_number: 3,
        title: "Claim Review",
        description: "TripSecure team reviews your claim",
        timeframe: "5-10 business days",
        requirements: "None",
      },
      {
        step_number: 4,
        title: "Claim Settlement",
        description: "Approved claims are processed for payment",
        timeframe: "2-5 business days after approval",
        requirements: "Valid bank account details",
      },
    ];

    // Get eligibility criteria - use form data for Travel, Life, and General Insurance
    // Always use hardcoded defaults for TripSecure
    const getEligibilityCriteria = () => {
      if (formData.insuranceType === "tripsecure") {
        // Always use hardcoded defaults for TripSecure
        return defaultEligibilityCriteria;
      }
      // Use provider-defined eligibility criteria for Travel, Life, and General
      // Return empty array if none provided (will use package-level criteria)
      const criteria = (formData.eligibilityCriteria || []).filter(
        (c) => c && c.trim() !== ""
      );
      return criteria.length > 0 ? criteria : [];
    };

    const eligibilityCriteria = getEligibilityCriteria();

    // Get terms and conditions - use form data for Travel, Life, and General Insurance
    // Always use hardcoded defaults for TripSecure
    const getTerms = () => {
      if (formData.insuranceType === "tripsecure") {
        // Always use hardcoded defaults for TripSecure
        return defaultTerms;
      }
      
      // Handle different input types for Travel, Life, and General
      const inputType = formData.termsInputType || "text";
      
      if (inputType === "url") {
        // Return the URL directly
        return formData.termsAndConditions?.trim() ||
          "This policy is governed by standard terms and conditions. Coverage is valid for the specified duration and route only.";
      } else {
        // Text input type
        return formData.termsAndConditions?.trim() ||
          "This policy is governed by standard terms and conditions. Coverage is valid for the specified duration and route only.";
      }
    };

    const terms = getTerms();

    // Get claim process - use form data for Travel, Life, and General Insurance
    // Always use hardcoded defaults for TripSecure
    const getClaimProcessSteps = () => {
      if (formData.insuranceType === "tripsecure") {
        // Always use hardcoded defaults for TripSecure
        return defaultClaimSteps;
      }
      // Use provider-defined claim process steps for Travel, Life, and General
      const steps = (formData.claimProcessSteps || []).filter(
        (step) =>
          step.title?.trim() &&
          step.description?.trim() &&
          step.timeframe?.trim()
      );
      if (steps.length > 0) {
        // Ensure step numbers are sequential
        return steps.map((step, index) => ({
          ...step,
          step_number: index + 1,
        }));
      }
      // Fallback for Travel/Life/General if no steps provided
      return [
        {
          step_number: 1,
          title: "Claim Process",
          description: "Contact support to initiate your claim process",
          timeframe: "As specified in policy",
          requirements: "Follow the process outlined in your policy",
        },
      ];
    };

    const claimProcessSteps = getClaimProcessSteps();

    // For non-tripsecure types, assign packages with per-package policy fields
    if (
      formData.insuranceType === "life" ||
      formData.insuranceType === "general" ||
      (formData.insuranceType === "travel" &&
        (formData.policyType === "travel_insurance" || !formData.policyType))
    ) {
      coverageDurationsInHours = (formData.packages || []).map((pkg, index) => {
        // Combine durationValue and durationUnit into a duration string
        const durationValue = pkg.durationValue || "";
        const durationUnit =
          pkg.durationUnit ||
          (formData.insuranceType === "travel" ? "hours" : "years");
        // Normalize duration unit to ensure proper format
        let normalizedUnit = durationUnit;
        if (durationUnit === "weekly") normalizedUnit = "weeks";
        else if (durationUnit === "monthly") normalizedUnit = "months";
        else if (durationUnit === "yearly") normalizedUnit = "years";
        const duration = durationValue ? `${durationValue} ${normalizedUnit}` : "";

        // Package-level benefits - check both field name formats
        const pkgBenefits = pkg.insurance_coverage_list || pkg.insuranceCoverageList || [];
        const packageBenefits =
          Array.isArray(pkgBenefits) && pkgBenefits.length > 0
            ? pkgBenefits.map((benefit) => ({
                title: benefit.title?.trim() || "",
                description: benefit.description?.trim() || "",
                limit: benefit.limit?.trim() || "",
                ...(benefit.type && benefit.type !== "general"
                  ? { coverage_type: benefit.type }
                  : {}),
              }))
            : (Array.isArray(benefits) && benefits.length > 0
                ? benefits
                : [
                    {
                      title: "Comprehensive Coverage",
                      description: "Full coverage as per policy terms and conditions",
                      limit: "As per policy",
                    },
                  ]);

        // Package-level exclusions - check both field name formats
        const pkgExclusions = pkg.exclusions || pkg.exclusionsList || [];
        const packageExclusions =
          Array.isArray(pkgExclusions) && pkgExclusions.length > 0
            ? (pkgExclusions || [])
                .map((exclusion) => {
                  const text = exclusion?.text?.trim
                    ? exclusion.text.trim()
                    : typeof exclusion === "string"
                    ? exclusion.trim()
                    : "";
                  return text;
                })
                .filter((e) => e !== "")
            : (Array.isArray(exclusions) && exclusions.length > 0
                ? exclusions
                : [
                    "Pre-existing conditions not covered",
                    "Intentional acts or illegal activities",
                    "War, terrorism, or nuclear risks",
                  ]);

        // Package-level eligibility criteria
        // Package-level eligibility criteria (check both field name formats)
        const pkgEligibility = pkg.eligibility_criteria || pkg.eligibilityCriteria || [];
        const packageEligibilityCriteria =
          Array.isArray(pkgEligibility) &&
          pkgEligibility.filter((c) => c && c.trim() !== "").length > 0
            ? pkgEligibility
                .filter((c) => c && c.trim() !== "")
                .map((c) => c.trim())
            : (Array.isArray(eligibilityCriteria) && eligibilityCriteria.length > 0
                ? eligibilityCriteria
                : ["Must be 18 years or older"]);

        // Package-level terms (check both field name formats)
        const packageTerms = (() => {
          const inputType = pkg.termsInputType || "text";
          const value = (pkg.terms || pkg.termsAndConditions)?.trim();
          if (value && inputType === "url") {
            return value;
          }
          if (value && inputType === "text") {
            return value;
          }
          return terms || "This policy is governed by standard terms and conditions. Coverage is valid for the specified duration and route only.";
        })();

        // Package-level claim process steps (check both field name formats)
        const pkgClaimSteps = pkg.claim_process_steps || pkg.claimProcessSteps || [];
        const packageClaimProcessSteps =
          Array.isArray(pkgClaimSteps) &&
          pkgClaimSteps.filter(
            (step) =>
              step.title?.trim() &&
              step.description?.trim() &&
              step.timeframe?.trim()
          ).length > 0
            ? pkgClaimSteps
                .filter(
                  (step) =>
                    step.title?.trim() &&
                    step.description?.trim() &&
                    step.timeframe?.trim()
                )
                .map((step, idx) => ({
                  ...step,
                  step_number: idx + 1,
                  title: step.title?.trim() || "",
                  description: step.description?.trim() || "",
                  timeframe: step.timeframe?.trim() || "",
                  requirements: step.requirements || "",
                }))
            : (Array.isArray(claimProcessSteps) && claimProcessSteps.length > 0
                ? claimProcessSteps
                : [
                    {
                      step_number: 1,
                      title: "Claim Process",
                      description: "Contact support to initiate your claim process",
                      timeframe: "As specified in policy",
                      requirements: "Follow the process outlined in your policy",
                    },
                  ]);

        const quoteRequestFieldsPayload = (pkg.quote_request_fields || []).map((f) => ({
          key: (f.key || "").trim(),
          label: (f.label || "").trim(),
          type: f.type || "text",
          required: !!f.required,
          ...(f.type === "select" ? { options: Array.isArray(f.options) ? f.options : (typeof f.options === "string" ? f.options.split(",").map((s) => s.trim()).filter(Boolean) : []) } : {}),
        })).filter((f) => f.key && f.label);

        return {
          product_name: pkg.productName?.trim() || "",
          premium: pkg.is_quote_only ? 0 : (parseFloat(pkg.premium) || 0),
          premium_duration: pkg.premiumDuration || "monthly",
          duration: duration,
          description: pkg.description?.trim() || "",
          max_claims: Number(pkg.max_claims) >= 1 ? Number(pkg.max_claims) : 3,
          is_quote_only: !!pkg.is_quote_only,
          quote_request_fields: pkg.is_quote_only ? quoteRequestFieldsPayload : [],
          // Per-package policy fields
          insurance_coverage_list: packageBenefits,
          exclusions: packageExclusions,
          eligibility_criteria: packageEligibilityCriteria,
          claim_process_steps: packageClaimProcessSteps,
          terms: packageTerms,
        };
      });
    }

    const defaultPolicyRules =
      "Standard TripSecure policy rules apply. Claims must be reported within 24 hours of incident. All documentation must be submitted within 7 days.";

    const defaultSupportContact = "support@tripsecure.com | +233 XXX XXX XXX";

    // Get the policy name based on insurance type
    const policyName = getPolicyName(
      formData.insuranceType,
      formData.subcategory
    );

    // Map insurance type to the correct format for backend
    const insuranceTypeMap = {
      tripsecure: "tripsecure_insurance",
      travel: "travel_insurance",
      life: "life_insurance",
      general: "general_insurance",
    };
    const insuranceType =
      insuranceTypeMap[formData.insuranceType] || "travel_insurance";

    // Determine policy_type based on insurance type
    let policyType = formData.policyType || "";
    if (formData.insuranceType === "tripsecure") {
      // Keep the policy type as is (intercity_travel, okada_rider, etc.)
      policyType = formData.policyType || "";
    } else if (formData.insuranceType === "life") {
      policyType = "life_insurance";
    } else if (formData.insuranceType === "general") {
      policyType = formData.subcategory || "";
    } else if (formData.insuranceType === "travel") {
      // For travel insurance, if policyType is travel_insurance or empty, set it to travel_insurance
      if (formData.policyType === "travel_insurance" || !formData.policyType) {
        policyType = "travel_insurance";
      } else {
        // Keep intercity_travel or other policy types
        policyType = formData.policyType || "";
      }
    }

    const shortDescription = insuranceType || "travel_insurance";

    // Get default description based on insurance type
    const getDefaultDescription = () => {
      if (formData.description?.trim()) {
        return formData.description.trim();
      }

      switch (formData.insuranceType) {
        case "life":
          return "Comprehensive life insurance coverage providing financial security and protection for you and your loved ones. Benefits include death benefit, cash value accumulation, and flexible payment options.";
        case "general":
          return "Comprehensive general insurance coverage designed to protect against various risks and uncertainties. Our flexible packages offer tailored protection to meet your specific needs and provide peace of mind.";
        case "travel":
        default:
          return "Comprehensive travel insurance coverage for your journey. Protection includes medical expenses, trip cancellation, and emergency assistance.";
      }
    };

    const defaultDescription = getDefaultDescription();

    // For non-TripSecure types, provide policy-level values
    // Use first package's data if available, otherwise use dummy values
    const policyLevelBenefits =
      formData.insuranceType === "tripsecure"
        ? benefits
        : benefits.length > 0
        ? benefits
        : [
            {
              title: "Coverage as specified in package",
              description: "Refer to individual package details",
              limit: "As per package",
            },
          ];

    const policyLevelExclusions =
      formData.insuranceType === "tripsecure"
        ? exclusions
        : exclusions.length > 0
        ? exclusions
        : ["Refer to package-specific exclusions"];

    // For non-TripSecure, try to use first package's eligibility criteria if available
    const policyLevelEligibility =
      formData.insuranceType === "tripsecure"
        ? eligibilityCriteria
        : eligibilityCriteria.length > 0
        ? eligibilityCriteria
        : coverageDurationsInHours.length > 0 &&
          Array.isArray(coverageDurationsInHours[0].eligibility_criteria) &&
          coverageDurationsInHours[0].eligibility_criteria.length > 0
        ? coverageDurationsInHours[0].eligibility_criteria
        : ["Must be 18 years or older"];

    return {
      // Include policy ID/slug for update
      policy_slug: policy_slug,

      insurance_type: insuranceType,
      policy_name: policyName,
      policy_type: policyType,
      description: defaultDescription,
      short_description: shortDescription,

      geography: geography,
      coverage_durations: coverageDurationsInHours,
      insurance_coverage_list: policyLevelBenefits,

      risk_level: "medium",
      eligibility_criteria: policyLevelEligibility,
      exclusions: policyLevelExclusions,

      terms: terms,
      claim_process_steps: claimProcessSteps,
      support_contact: defaultSupportContact,
      policy_rules: defaultPolicyRules,

      max_claims: formData.insuranceType === "tripsecure"
        ? (Number(formData.max_claims) || 3)
        : (coverageDurationsInHours?.length > 0
            ? Math.max(...coverageDurationsInHours.map((d) => Number(d.max_claims) || 3))
            : 3),
      waiting_period: 0,

      has_policy_image: false,
      policy_image_base64: "",
      documents: [],
    };
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setSubmitError(null);
    if (!validateAllSteps()) {
      const firstErrorStep = Object.keys(validationErrors).length > 0 ? 1 : 1;
      setCurrentStep(firstErrorStep);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = transformFormDataToPayload();

      const result = await policyService.updatePolicy(payload, policy_slug);
      console.log("update_response", result?.data?.data);

      if (result.status) {
        setSubmitSuccess(true);
        setHasUnsavedChanges(false);

        // Show success message
        setTimeout(() => {
          navigate("/policies");
        }, 2000);
      } else {
        setSubmitError(result.message || "Failed to update policy");
      }
    } catch (error) {
      console.error("Policy update error:", error);
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while fetching policy
  if (isLoadingPolicy) {
    return (
      <Loader
        fullScreen={true}
        text="Loading policy details..."
        subText="Please wait while we fetch your policy information"
      />
    );
  }

  // Show error state if policy failed to load
  if (loadError) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 mx-auto max-w-md text-center bg-white rounded-2xl shadow-xl"
        >
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            Failed to Load Policy
          </h2>
          <p className="mb-6 text-gray-600">{loadError}</p>
          <div className="flex justify-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              <RefreshCw className="mr-2 w-4 h-4" />
              Retry
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/policies")}
              className="flex items-center px-4 py-2 text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Policies
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50">
      {/* Account Status Modal */}
      <AccountStatusModal />

      {/* Success Notification */}
      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-4 left-1/2 z-50 transform -translate-x-1/2"
          >
            <div className="flex items-center px-6 py-4 text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <CheckCircle className="mr-3 w-6 h-6" />
              <div>
                <div className="font-semibold">
                  Policy Updated Successfully!
                </div>
                <div className="text-sm opacity-90">
                  Redirecting to policies page...
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Notification */}
      <AnimatePresence>
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-4 left-1/2 z-50 transform -translate-x-1/2"
          >
            <div className="flex items-center px-6 py-4 text-white bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-lg">
              <AlertTriangle className="mr-3 w-6 h-6" />
              <div>
                <div className="font-semibold">Update Failed</div>
                <div className="text-sm opacity-90">{submitError}</div>
              </div>
              <button
                onClick={() => setSubmitError(null)}
                className="p-1 ml-4 rounded-full transition-colors hover:bg-white hover:bg-opacity-20"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={isBlocked ? "pointer-events-none opacity-30 blur-sm transition-all duration-300" : ""}>
        {/* Enhanced Header for Edit Mode */}
        <div className="px-4 pt-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex-shrink-0 p-1.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-md">
                <Edit3 className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg font-semibold text-gray-900 truncate sm:text-xl">
                    Edit Policy
                  </h1>
                  {hasUnsavedChanges && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex items-center px-2 py-0.5 bg-orange-100 rounded-full"
                    >
                      <div className="mr-1.5 w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
                      <span className="text-xs font-medium text-orange-700">
                        Unsaved Changes
                      </span>
                    </motion.div>
                  )}
                </div>
                <p className="text-xs text-gray-500 sm:text-sm truncate">
                  Modify and update your "{formData.policyName}" policy settings
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-3">
              <div className="hidden text-right sm:block">
                <div className="text-xs font-medium text-gray-900 sm:text-sm">
                  Step {(() => {
                    const isTripSecure = formData.insuranceType === "tripsecure";
                    const visibleSteps = steps.filter(
                      (step) => !(isTripSecure && step.id === 4)
                    );
                    const currentDisplayStep = isTripSecure && currentStep === 5 ? 4 : currentStep;
                    return `${currentDisplayStep} of ${visibleSteps.length}`;
                  })()}
                </div>
                <div className="text-xs text-gray-500">
                  {Math.round((() => {
                    const isTripSecure = formData.insuranceType === "tripsecure";
                    const visibleSteps = steps.filter(
                      (step) => !(isTripSecure && step.id === 4)
                    );
                    const currentDisplayStep = isTripSecure && currentStep === 5 ? 4 : currentStep;
                    return ((currentDisplayStep - 1) / (visibleSteps.length - 1)) * 100;
                  })())}% Complete
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNavigation("/policies")}
                className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-lg shadow-sm transition-all duration-200 hover:shadow-md hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </motion.button>
            </div>
          </div>

          {/* Policy Status Indicator */}
          {originalPolicy?.data?.data?.status && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="p-2 mt-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-3"
            >
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <span className="text-xs font-medium text-gray-700 sm:text-sm">
                    Current Status:
                  </span>
                  <div
                    className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                      originalPolicy.data.data.status === "approved"
                        ? "bg-green-100 text-green-700"
                        : originalPolicy.data.data.status === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {originalPolicy.data.data.status === "approved" && (
                      <CheckCircle className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                    {originalPolicy.data.data.status === "pending" && (
                      <AlertCircle className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                    {originalPolicy.data.data.status === "rejected" && (
                      <X className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                    {originalPolicy.data.data.status.charAt(0).toUpperCase() +
                      originalPolicy.data.data.status.slice(1)}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Last updated: {new Date().toLocaleDateString()}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
        </div>

        <StepperNav
          steps={steps}
          currentStep={currentStep}
          goToStep={goToStep}
          insuranceType={formData.insuranceType}
          termsStepId={4}
        />

        {/* Main Content */}
        <div className="px-4 pt-4 pb-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="overflow-hidden relative bg-white rounded-2xl border border-gray-200 shadow-lg">
          {/* Loading Overlay */}
          <AnimatePresence>
            {isSubmitting && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex absolute inset-0 z-10 justify-center items-center bg-white bg-opacity-90 backdrop-blur-sm"
              >
                <div className="text-center">
                  <Loader2 className="mx-auto mb-4 w-8 h-8 text-orange-600 animate-spin" />
                  <div className="text-lg font-semibold text-gray-900">
                    Updating Your Policy...
                  </div>
                  <div className="mb-4 text-sm text-gray-600">
                    Please wait while we save your changes
                  </div>
                  <div className="overflow-hidden w-64 h-2 bg-gray-200 rounded-full">
                    <motion.div
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-600 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 2, ease: "easeInOut" }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="p-4 sm:p-6">
            <AnimatePresence mode="wait">{renderCurrentStep()}</AnimatePresence>
          </div>

          {/* Navigation Footer */}
          <div className="px-4 py-4 bg-gray-50 border-t border-gray-200 sm:px-6 lg:px-8 sm:py-6">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-200 ${
                  currentStep === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Previous
              </motion.button>

              <div className="flex space-x-3">
                {currentStep === 5 ? (
                  <motion.button
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`flex items-center justify-center px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-white rounded-xl shadow-lg transition-all duration-200 w-full sm:w-auto ${
                      isSubmitting
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        <span className="hidden sm:inline">
                          Updating Policy...
                        </span>
                        <span className="sm:hidden">Updating...</span>
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 w-4 h-4" />
                        <span className="hidden sm:inline">Update Policy</span>
                        <span className="sm:hidden">Update</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    onClick={nextStep}
                    disabled={isSubmitting}
                    className={`flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 font-medium text-white rounded-xl shadow-lg transition-all duration-200 w-full sm:w-auto ${
                      isSubmitting
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                    }`}
                  >
                    Next
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnsavedModal}
        onClose={handleUnsavedCancel}
        onConfirm={handleUnsavedConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?"
        confirmText="Leave Page"
        cancelText="Stay Here"
        variant="warning"
      />
    </div>
  );
}
